import { getApp, getApps, initializeApp } from "firebase/app";
import type { FirebaseApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, signInWithPopup } from "firebase/auth";
import type { Auth } from "firebase/auth";

// These values are public by design. Firebase's web config is shipped inside
// the JS bundle of every Firebase web app, and access is controlled by the
// project's auth settings and security rules rather than by hiding the config.
// CRA inlines REACT_APP_* at build time, so the Dockerfile has to pass them as
// build args for the production image to end up with them.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Popup errors that just mean "the user changed their mind". They are a normal
// outcome, not a failure, so the caller stays quiet instead of toasting.
const DISMISSED_POPUP_CODES = [
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
];

export function isFirebaseConfigured(): boolean {
  const { apiKey, authDomain, projectId, appId } = firebaseConfig;
  return Boolean(apiKey && authDomain && projectId && appId);
}

/**
 * The initialised Firebase auth instance, or null when the env vars are absent.
 * Initialisation is lazy and happens at most once, so a build without the vars
 * still works and simply never offers Firebase sign-in.
 */
export function getFirebaseAuth(): Auth | null {
  if (!isFirebaseConfigured()) {
    return null;
  }
  const app: FirebaseApp = getApps().length
    ? getApp()
    : initializeApp(firebaseConfig);
  return getAuth(app);
}

export function isDismissedPopup(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" && DISMISSED_POPUP_CODES.includes(code);
}

/**
 * Run the Google popup flow and return the resulting Firebase ID token. The
 * token is meant to go straight to `POST /auth/firebase`, which verifies it and
 * hands back an OnlyDash JWT.
 */
export async function signInWithGoogle(): Promise<string> {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new Error("Firebase is not configured");
  }
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  return result.user.getIdToken();
}
